import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { VC } from '@semios/app-platform-value-type-definitions'
import type { TIrrigationDeviceStatus, TLngLat, TValuesCurrentPoints } from 'App/Map/types'
import { translate } from 'i18n/i18n'
import { isEmpty, isNil } from 'lodash'
import { colors } from 'settings/colors'
import { MAP_VISUAL } from 'stores/mapControlsStore/types'
import { apiFetch } from 'utils/apiFetch'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import type { TGetCacheUpdatesFromResponseParameters, TGetCacheUpdatesFromResponseReturn } from './_types'
import { getHeatmapCSS } from './_utils/getHeatmapCSS'
import { getIrrigationStatusReturn } from './_utils/getIrrigationStatusReturn'
import { getValueType } from './_utils/getValueType'
import {
  Z_INDEX_HAS_DATA,
  Z_INDEX_NO_DATA,
  Z_INDEX_NOT_APPLICABLE,
  Z_INDEX_OFF,
} from './_utils/irrigationActivityConstants'
import { setHeatmapExtremesFromArrayOfValues } from './_utils/setHeatmapExtremesFromArrayOfValues'

const currentValueTypesForFlowMeter = [
  'irrigationActivity_today_cumulativeFlowMeter_gallons',
  'irrigationActivity_prev7Days_cumulativeFlowMeter_gallons',
  'irrigationActivity_season_cumulativeFlowMeter_gallons',
  'irrigationActivity_YTD_cumulativeFlowMeter_gallons',
  'irrigationActivity_currentFlowRate_gpm',
  'irrigationActivity_currentFlowStatus',
]

const makeApiArgs = (
  processedCaches: TGetCacheUpdatesFromResponseParameters['processedCaches'],
): routes.ValuesCurrent.Request => {
  const pointValueType = getValueType(MAP_VISUAL.POINT, 'irrigation_activity_flow_meters')
  const propertyValueType = getValueType(MAP_VISUAL.PROPERTY, 'irrigation_activity_flow_meters')
  const returner: routes.ValuesCurrent.Request = {}
  const pointsToFetch: TLngLat[] = []

  if (
    pointValueType &&
    currentValueTypesForFlowMeter.includes(pointValueType) &&
    !!processedCaches.irrigationFlowMeterValues.itemsWithinView.length
  ) {
    processedCaches.irrigationFlowMeterValues.itemsWithinView.forEach((s) => {
      pointsToFetch.push(s.meta.lngLat)
    })

    if (!!pointsToFetch.length) {
      returner.points = { lngLats: pointsToFetch, values: { [pointValueType]: true } }
    }
  }

  if (
    propertyValueType &&
    VC.valuesCurrentValueTypes_Properties.includes(propertyValueType) &&
    !!processedCaches.propertyValues.itemsWithinView.length
  ) {
    returner.properties = {
      propertyIds: processedCaches.propertyValues.itemsWithinView.map((p) => Number(p.id)),
      values: { [propertyValueType]: true },
    }
  }

  return returner
}

export const getResponseAndShapeForCacheUpdate = async ({
  cacheKeys,
  processedCaches,
}: TGetCacheUpdatesFromResponseParameters): Promise<TGetCacheUpdatesFromResponseReturn> => {
  const args = makeApiArgs(processedCaches)

  if (isEmpty(args)) return {}

  const response = await apiFetch({ url: '/values-current', body: args })

  const pointValueType = getValueType(
    MAP_VISUAL.POINT,
    'irrigation_activity_flow_meters',
  ) as TValuesCurrentPoints

  const propertyValueType = getValueType(
    MAP_VISUAL.PROPERTY,
    'irrigation_activity_flow_meters',
  ) as TValuesCurrentPoints

  const allValuesForHeatmap: number[] = []
  const returner: TGetCacheUpdatesFromResponseReturn = {}

  if (processedCaches.irrigationFlowMeterValues.itemsWithinView.length) {
    const itemIdsWithinView: string[] = []

    const itemsWithinViewThatNowHaveValues =
      processedCaches.irrigationFlowMeterValues.itemsWithinView.flatMap((flowMeter) => {
        itemIdsWithinView.push(flowMeter.id)

        const value = response?.points?.[flowMeter.meta.lngLat]?.[pointValueType]?.value ?? null

        if (!isNil(value)) {
          const isValueTypeCurrentStatus = pointValueType === 'irrigationActivity_currentFlowStatus'

          if (isValueTypeCurrentStatus) {
            return {
              id: String(flowMeter.id),
              value: {
                [pointValueType]: getIrrigationStatusReturn(
                  String(value).toLowerCase() as TIrrigationDeviceStatus,
                ),
              },
            }
          }

          const valueAsNumber = Number(value)
          const isValueTypeFlowRate = pointValueType === 'irrigationActivity_currentFlowRate_gpm'

          const cumulativeFlowMeter = isValueTypeFlowRate
            ? unitConverter.flowRate(valueAsNumber).value()
            : unitConverter.flowMeter(valueAsNumber).value()

          if (!isNil(value)) allValuesForHeatmap.push(cumulativeFlowMeter as number)

          return {
            id: String(flowMeter.id),
            value: {
              [pointValueType]: {
                children: cumulativeFlowMeter,
                getContainerCSS: () => getHeatmapCSS({ value: cumulativeFlowMeter }),
                baseZIndex: cumulativeFlowMeter && cumulativeFlowMeter > 0 ? Z_INDEX_HAS_DATA : Z_INDEX_OFF,
              },
            },
          }
        }

        return {
          id: String(flowMeter.id),
          value: {
            [pointValueType]: {
              children: translate.phrases.templates('-'),
              getContainerCSS: () => ({ backgroundColor: colors.grey200, color: colors.midnight }),
              baseZIndex: Z_INDEX_NO_DATA,
            },
          },
        }
      })

    returner.irrigationFlowMeters = {
      itemsWithinViewThatNowHaveValues,
      cacheKey: cacheKeys.irrigationFlowMeterValuesCacheKey,
      itemIdsWithinView,
    }
  }

  const itemsWithinViewThatNowHaveValues = processedCaches.propertyValues.itemsWithinView.map((property) => {
    return {
      id: String(property.id),
      value: {
        [propertyValueType]: {
          baseZIndex: Z_INDEX_NOT_APPLICABLE,
          children: property.meta.propertyName,
          getContainerCSS: () => ({ backgroundColor: colors.midnight, color: colors.white }),
          onHover: false,
        },
      },
    }
  })

  setHeatmapExtremesFromArrayOfValues({
    heatmapColoring: [
      colors.irrigationHeatmapSoftBlue,
      colors.irrigationHeatmapMediumBlue,
      colors.irrigationHeatmapStrongBlue,
    ],
    values: allValuesForHeatmap,
  })

  returner.properties = {
    itemsWithinViewThatNowHaveValues,
    cacheKey: cacheKeys.propertyCacheKey,
    itemIdsWithinView: processedCaches.propertyValues.itemIdsWithinView,
  }

  return returner
}
